import type {
  MRT_Cell,
  MRT_HeaderGroup,
  MRT_Row,
  MRT_TableInstance
} from "mantine-react-table-open";
import { getColSum } from "../../../../mantine/mrt/utils/getColSum";
import { getColumnDataKey } from "../../../../mantine/mrt/utils/getColumnDataKey";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import type { ExcelCompatibleTableData } from "../../../../utils/files/downloadAsExcel";
import type { EnergyRevenuesListItem } from "../../RevenueMonitoring.types";

const formatField = (value: number | null, decimalScale = 2): number | null =>
  value !== null && value !== undefined
    ? Math.round(value * Math.pow(10, decimalScale)) /
      Math.pow(10, decimalScale)
    : null;

function formatCellValue(
  cell: MRT_Cell<EnergyRevenuesListItem>,
  row: MRT_Row<EnergyRevenuesListItem>
): string | number {
  const id = cell.column.columnDef.id;

  if (id === "period_start") {
    return backendDateOrDateTimeToLuxonDateTime(
      row.original[id]
    ).toLocaleString({ month: "short", year: "numeric" });
  }

  const decimalScale = id === "revenue_per_kwh" ? 3 : 2;

  return formatField(cell.getValue<number>(), decimalScale) ?? "";
}

function getGroupHeaders(
  headerGroupDefinitions: Array<MRT_HeaderGroup<EnergyRevenuesListItem>>
) {
  return headerGroupDefinitions[0].headers
    .map((header) => {
      const groupCols = [header.column.columnDef.header];
      if (header.colSpan > 1) {
        groupCols.push(...Array(header.colSpan - 1).fill(""));
      }
      return groupCols;
    })
    .flat();
}

function getSumRow(
  table: MRT_TableInstance<EnergyRevenuesListItem>,
  singleColumnDataKeys: Array<string>
) {
  return singleColumnDataKeys.map((dataKey) => {
    if (dataKey === "period_start") {
      return "Summe";
    }

    const value = getColSum(table, dataKey);

    const formattedValue = formatField(value || null);

    return formattedValue ?? "–";
  });
}

/**
 * Prepares the revenue monitoring table data for export/download.
 * Reformats the content from provided Mantine React Table instance so that it can be exported to Excel.
 *
 * @param table  Mantine React Table instance containing the revenue monitoring data.
 * @returns      2D array representing the table data, including headers and sum row.
 */
export function prepareTableDataForExport(
  table: MRT_TableInstance<EnergyRevenuesListItem>
): ExcelCompatibleTableData {
  const rowData = table
    .getPrePaginationRowModel()
    .rows.map((row) =>
      row.getVisibleCells().map((cell) => formatCellValue(cell, row))
    );

  const headerGroupDefinitions = table.getHeaderGroups();

  const groupHeaders = getGroupHeaders(headerGroupDefinitions);
  const singleColumnHeaders = headerGroupDefinitions[1].headers.map(
    (header) => header.column.columnDef.header
  );
  const singleColumnDataKeys = headerGroupDefinitions[1].headers.map((header) =>
    getColumnDataKey(header.column.columnDef)
  );

  const sumRow = getSumRow(table, singleColumnDataKeys);

  return [groupHeaders, singleColumnHeaders, ...rowData, sumRow];
}
